import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UUID } from "node:crypto";
import api from "@/app/api";
import { routeKeys } from "../keys/routes";
import type {
  RouteCreate,
  RouteUpdate,
  RouteFilter,
} from "@/app/api/types/route";

// Route queries
export function useRoutes(filters?: RouteFilter) {
  return useQuery({
    queryKey: routeKeys.list(filters),
    queryFn: () => api.routes.list(filters),
  });
}

const MISSING_ROUTE_ID = "" as UUID;

export function useRoute(id: UUID | null) {
  const routeId = id ?? MISSING_ROUTE_ID;
  return useQuery({
    queryKey: routeKeys.detail(routeId),
    queryFn: () => api.routes.get(routeId),
    enabled: !!id,
  });
}

// Route mutations
export function useCreateRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RouteCreate) => api.routes.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["routes", "global"] });
    },
  });
}

export function useUpdateRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: UUID; data: RouteUpdate }) =>
      api.routes.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: routeKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: routeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["routes", "global"] });
    },
  });
}

export function useDeleteRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => api.routes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["routes", "global"] });
    },
  });
}
